import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import * as graphql from '@treams/treams-network';
import {
	Card,
	DeleteIcon,
	DocumentIcon,
	DownloadIcon,
	Empty,
	FlexGap,
	Heading,
	IconButton,
	ListItem,
	Spinner,
	tokens,
} from '@treams/treams-ui';

function stopRowClick(event: MouseEvent) {
	event.stopPropagation();
}

function UserDownload(props: { user: graphql.User; download: graphql.UserDownload }) {
	const { t } = useTranslation();

	const [commit, { loading }] = graphql.useDeleteUserDownloadMutation({
		variables: { input: { id: props.download.id } },
	});

	const onDownloadClick = () => {
		if (props.download.downloadLink) {
			window.open(props.download.downloadLink);
		}
	};

	const status =
		props.download.status === graphql.UserDownloadStatus.Pending
			? t('user.downloads.status.pending')
			: props.download.status === graphql.UserDownloadStatus.Failed
				? t(`user.downloads.status.failed.${props.download.failedReason}`)
				: props.download.status === graphql.UserDownloadStatus.Running
					? t('user.downloads.status.running', {
							progress: (props.download.progress * 100).toFixed(2),
						})
					: props.download.status === graphql.UserDownloadStatus.Success
						? t('user.downloads.status.success')
						: undefined;

	return (
		<ListItem
			icon={<DocumentIcon />}
			title={props.download.name}
			meta={status}
			onClick={props.download.downloadLink ? onDownloadClick : undefined}
			trailing={
				<FlexGap alignItems="center" gap={tokens.gap.element} onClick={stopRowClick}>
					{loading ? (
						<Spinner />
					) : (
						<>
							{props.download.downloadLink && (
								<IconButton icon={<DownloadIcon />} type="secondary" onClick={onDownloadClick} />
							)}
							{props.download.viewerCanDelete && (
								<IconButton
									icon={<DeleteIcon />}
									type="destructive"
									onClick={() => {
										commit();
									}}
								/>
							)}
						</>
					)}
				</FlexGap>
			}
		/>
	);
}

export default function UserDownloads(props: { user: graphql.User }) {
	const { t } = useTranslation();

	const query = graphql.useUserDownloadsQuery({ variables: { id: props.user.id } });

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.group}>
			<Heading>{t('user.downloads.title')}</Heading>
			{query.data && query.data.user.downloads.length > 0 ? (
				<Card p={0}>
					{query.data.user.downloads.map((doc) => (
						<UserDownload key={doc.id} user={props.user} download={doc as graphql.UserDownload} />
					))}
				</Card>
			) : query.loading ? (
				<Spinner centered />
			) : (
				<Empty.Card icon={<DocumentIcon />} title={t('user.downloads.empty')} />
			)}
		</FlexGap>
	);
}
